import { useState } from 'react';
import {
  doc,
  getDoc,
  setDoc,
  collection,
  query,
  where,
  getDocs,
  orderBy,
  limit,
} from 'firebase/firestore';
import { useQuery, useMutation } from '@tanstack/react-query';
import { db } from '../../lib/firebase';
import type { HomeAreaTab, Listing } from '@shared/types';
import toast from 'react-hot-toast';

const SETTINGS_DOC_ID = 'default';
const PUBLIC_HOME_DOC = 'home';

function newAreaRow(): HomeAreaTab {
  return {
    id: `area_${Date.now()}`,
    label: '',
    city: '',
    description: '',
    enabled: true,
    sortOrder: 0,
  };
}

export default function Settings() {
  const [enquiryEmail, setEnquiryEmail] = useState('info@leisurebreaks.co.za');
  const [homeFeaturedIds, setHomeFeaturedIds] = useState<string[]>([]);
  const [homeAreas, setHomeAreas] = useState<HomeAreaTab[]>([]);

  const { isLoading } = useQuery({
    queryKey: ['admin-settings-full'],
    queryFn: async () => {
      const ref = doc(db, 'settings', SETTINGS_DOC_ID);
      const snap = await getDoc(ref);
      if (snap.exists()) {
        const data = snap.data() as { enquiryRecipientEmail?: string };
        if (data.enquiryRecipientEmail) {
          setEnquiryEmail(data.enquiryRecipientEmail);
        }
      }

      const pub = await getDoc(doc(db, 'publicSettings', PUBLIC_HOME_DOC));
      if (pub.exists()) {
        const d = pub.data() as {
          homeFeaturedListingIds?: string[];
          homeAreas?: HomeAreaTab[];
        };
        if (d.homeFeaturedListingIds?.length) {
          setHomeFeaturedIds([...d.homeFeaturedListingIds]);
        }
        if (d.homeAreas?.length) {
          setHomeAreas(d.homeAreas.map((a) => ({ ...a })));
        }
      }

      return {};
    },
  });

  const { data: publishedListings = [] } = useQuery({
    queryKey: ['admin-settings-listings'],
    queryFn: async () => {
      let q = query(
        collection(db, 'listings'),
        where('status', '==', 'published'),
        orderBy('title', 'asc'),
        limit(200)
      );
      try {
        const snap = await getDocs(q);
        return snap.docs.map((d) => ({ id: d.id, ...d.data() })) as Listing[];
      } catch {
        const snap = await getDocs(
          query(collection(db, 'listings'), where('status', '==', 'published'), limit(200))
        );
        const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() })) as Listing[];
        return rows.sort((a, b) => (a.title || '').localeCompare(b.title || ''));
      }
    },
  });

  const enquiryMutation = useMutation({
    mutationFn: async (email: string) => {
      const ref = doc(db, 'settings', SETTINGS_DOC_ID);
      await setDoc(
        ref,
        {
          enquiryRecipientEmail: email,
        },
        { merge: true }
      );
    },
    onSuccess: () => toast.success('Enquiry email saved'),
    onError: (err: any) => toast.error(err?.message || 'Failed to save'),
  });

  const homeMutation = useMutation({
    mutationFn: async () => {
      const ref = doc(db, 'publicSettings', PUBLIC_HOME_DOC);
      const areasClean = homeAreas
        .filter((a) => a.label.trim() && a.city.trim())
        .map((a, i) => ({
          ...a,
          id: a.id || `area_${i}`,
          label: a.label.trim(),
          city: a.city.trim(),
          description: (a.description || '').trim() || undefined,
          sortOrder: a.sortOrder ?? i,
          enabled: a.enabled !== false,
          activeFrom: a.activeFrom?.trim() || undefined,
          activeUntil: a.activeUntil?.trim() || undefined,
        }));
      await setDoc(
        ref,
        {
          homeFeaturedListingIds: homeFeaturedIds.filter(Boolean),
          homeAreas: areasClean,
          updatedAt: new Date().toISOString(),
        },
        { merge: true }
      );
    },
    onSuccess: () => toast.success('Home page configuration saved'),
    onError: (err: any) => toast.error(err?.message || 'Failed to save home settings'),
  });

  const handleEnquirySubmit = (e: React.FormEvent) => {
    e.preventDefault();
    enquiryMutation.mutate(enquiryEmail.trim());
  };

  const addFeatured = (listingId: string) => {
    if (!listingId || homeFeaturedIds.includes(listingId)) return;
    setHomeFeaturedIds([...homeFeaturedIds, listingId]);
  };

  const removeFeatured = (listingId: string) => {
    setHomeFeaturedIds(homeFeaturedIds.filter((id) => id !== listingId));
  };

  const moveFeatured = (index: number, dir: -1 | 1) => {
    const j = index + dir;
    if (j < 0 || j >= homeFeaturedIds.length) return;
    const next = [...homeFeaturedIds];
    [next[index], next[j]] = [next[j], next[index]];
    setHomeFeaturedIds(next);
  };

  const listingTitle = (id: string) =>
    publishedListings.find((l) => l.id === id)?.title || id;

  return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-8">
        <h1 className="text-3xl font-bold mb-2">Settings</h1>
        <p className="text-gray-600 mb-6">
          Manage enquiry routing, home page featured units, and browse-by-area tabs.
      </p>

      <section className="bg-white rounded-lg shadow p-6">
        <h2 className="text-xl font-semibold mb-3">Payments & Integrations</h2>
        <p className="text-sm text-gray-600 mb-4">
          PayFast is configured on the backend using secure environment variables. To change
          merchant details or credentials, contact your developer or hosting provider.
        </p>
        <ul className="list-disc list-inside text-sm text-gray-600 space-y-1">
          <li>Provider: PayFast (server-side integration)</li>
          <li>Checkout: guests receive a secure PayFast payment link from the admin portal</li>
          <li>ITN / webhooks: handled via Cloud Functions</li>
        </ul>
      </section>

      <section className="bg-white rounded-lg shadow p-6">
        <h2 className="text-xl font-semibold mb-3">Home page: featured units</h2>
        <p className="text-sm text-gray-600 mb-4">
          Choose which published listings appear in the &quot;Featured Properties&quot; section and in
          what order. If this list is empty, the site falls back to listings marked Featured or the
          latest published units.
        </p>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Add a listing</label>
            <select
              className="input"
              defaultValue=""
              onChange={(e) => {
                const v = e.target.value;
                if (v) {
                  addFeatured(v);
                  e.target.value = '';
                }
              }}
            >
              <option value="">Select a property to add…</option>
              {publishedListings.map((l) => (
                <option key={l.id} value={l.id} disabled={homeFeaturedIds.includes(l.id)}>
                  {l.title} - {l.location?.city}
                </option>
              ))}
            </select>
          </div>
          <div>
            <p className="text-sm font-medium text-gray-700 mb-2">Order on home (top first)</p>
            {homeFeaturedIds.length === 0 ? (
              <p className="text-sm text-gray-500">None selected.</p>
            ) : (
              <ul className="space-y-2">
                {homeFeaturedIds.map((fid, idx) => (
                  <li
                    key={fid}
                    className="flex items-center gap-2 flex-wrap border rounded-lg px-3 py-2 bg-gray-50"
                  >
                    <span className="flex-1 text-sm">{listingTitle(fid)}</span>
                    <button
                      type="button"
                      className="text-xs btn btn-secondary px-2 py-1"
                      onClick={() => moveFeatured(idx, -1)}
                      disabled={idx === 0}
                    >
                      Up
                    </button>
                    <button
                      type="button"
                      className="text-xs btn btn-secondary px-2 py-1"
                      onClick={() => moveFeatured(idx, 1)}
                      disabled={idx === homeFeaturedIds.length - 1}
                    >
                      Down
                    </button>
                    <button
                      type="button"
                      className="text-xs text-red-600 px-2"
                      onClick={() => removeFeatured(fid)}
                    >
                      Remove
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
        <button
          type="button"
          className="btn btn-primary mt-4"
          disabled={homeMutation.isPending || isLoading}
          onClick={() => homeMutation.mutate()}
        >
          {homeMutation.isPending ? 'Saving…' : 'Save home page layout'}
        </button>
      </section>

      <section className="bg-white rounded-lg shadow p-6">
        <h2 className="text-xl font-semibold mb-3">Home page: Browse by area</h2>
        <p className="text-sm text-gray-600 mb-4">
          Control which area tabs appear, their labels, and optional date ranges (show a tab only
          between Active from and Active until, inclusive). Leave dates empty to always show. Listings
          are filtered by <strong>City</strong> matching <code>location.city</code> on each listing.
        </p>
        <div className="space-y-4">
          {homeAreas.map((row, idx) => (
            <div
              key={row.id}
              className="grid grid-cols-1 md:grid-cols-12 gap-2 border rounded-lg p-3 bg-gray-50 items-end"
            >
              <div className="md:col-span-2">
                <label className="block text-xs text-gray-500 mb-1">Tab label</label>
                <input
                  className="input text-sm"
                  value={row.label}
                  onChange={(e) => {
                    const next = [...homeAreas];
                    next[idx] = { ...row, label: e.target.value };
                    setHomeAreas(next);
                  }}
                  placeholder="e.g. Cape Town"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-xs text-gray-500 mb-1">City (match)</label>
                <input
                  className="input text-sm"
                  value={row.city}
                  onChange={(e) => {
                    const next = [...homeAreas];
                    next[idx] = { ...row, city: e.target.value };
                    setHomeAreas(next);
                  }}
                  placeholder="Cape Town"
                />
              </div>
              <div className="md:col-span-3">
                <label className="block text-xs text-gray-500 mb-1">Description</label>
                <input
                  className="input text-sm"
                  value={row.description || ''}
                  onChange={(e) => {
                    const next = [...homeAreas];
                    next[idx] = { ...row, description: e.target.value };
                    setHomeAreas(next);
                  }}
                  placeholder="Short blurb under tabs"
                />
              </div>
              <div className="md:col-span-1">
                <label className="block text-xs text-gray-500 mb-1">Sort</label>
                <input
                  type="number"
                  className="input text-sm"
                  value={row.sortOrder ?? 0}
                  onChange={(e) => {
                    const next = [...homeAreas];
                    next[idx] = { ...row, sortOrder: parseInt(e.target.value, 10) || 0 };
                    setHomeAreas(next);
                  }}
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-xs text-gray-500 mb-1">Active from</label>
                <input
                  type="date"
                  className="input text-sm"
                  value={row.activeFrom || ''}
                  onChange={(e) => {
                    const next = [...homeAreas];
                    next[idx] = { ...row, activeFrom: e.target.value || undefined };
                    setHomeAreas(next);
                  }}
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-xs text-gray-500 mb-1">Active until</label>
                <input
                  type="date"
                  className="input text-sm"
                  value={row.activeUntil || ''}
                  onChange={(e) => {
                    const next = [...homeAreas];
                    next[idx] = { ...row, activeUntil: e.target.value || undefined };
                    setHomeAreas(next);
                  }}
                />
              </div>
              <div className="md:col-span-12 flex items-center gap-3 pt-1">
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={row.enabled !== false}
                    onChange={(e) => {
                      const next = [...homeAreas];
                      next[idx] = { ...row, enabled: e.target.checked };
                      setHomeAreas(next);
                    }}
                  />
                  Enabled
                </label>
                <button
                  type="button"
                  className="text-sm text-red-600"
                  onClick={() => setHomeAreas(homeAreas.filter((_, i) => i !== idx))}
                >
                  Remove row
                </button>
              </div>
            </div>
          ))}
          <button
            type="button"
            className="btn btn-secondary text-sm"
            onClick={() => setHomeAreas([...homeAreas, newAreaRow()])}
          >
            + Add area tab
          </button>
        </div>
        <button
          type="button"
          className="btn btn-primary mt-4"
          disabled={homeMutation.isPending || isLoading}
          onClick={() => homeMutation.mutate()}
        >
          {homeMutation.isPending ? 'Saving…' : 'Save home page layout'}
        </button>
      </section>

      <section className="bg-white rounded-lg shadow p-6">
        <h2 className="text-xl font-semibold mb-3">Enquiry settings</h2>
        <p className="text-sm text-gray-600 mb-4">
          Website enquiries are delivered to <strong>info@leisurebreaks.co.za</strong> using the
          Leisure Breaks branded email layout.
        </p>
        <form onSubmit={handleEnquirySubmit} className="space-y-4 max-w-lg">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Enquiry recipient email
            </label>
            <input
              type="email"
              className="input"
              value={enquiryEmail}
              onChange={(e) => setEnquiryEmail(e.target.value)}
              placeholder="info@leisurebreaks.co.za"
              required
            />
          </div>
          <button type="submit" className="btn btn-primary" disabled={isLoading || enquiryMutation.isPending}>
            {enquiryMutation.isPending ? 'Saving...' : 'Save enquiry email'}
          </button>
        </form>
      </section>
    </div>
  );
}
